/**
 * Reject the offer, set for a jury trial, and demand the evidence.
 *
 * Lloyd to the whole team, Sep 1 2026:
 *
 * > *"if the only offer is a guilty plea, we owe it to our clients both to push back and demand to
 * > see all the evidence the state has against our clients — this is especially true for CDL cases
 * > ... politely set the case for a jury trial and make sure we sent a request for all the
 * > evidence."*
 *
 * He described this button to ten members of staff before it existed. This is it.
 *
 * ## Readiness is shown before the button, not after
 *
 * The endpoint still refuses a case with no client name or no court, and that refusal is real work
 * for somebody. Meeting it for the first time as an error, after pressing a button labelled "reject
 * the offer", is how a screen teaches staff to distrust it. So the blockers are fetched on load and
 * the button is disabled with the reasons underneath it.
 *
 * A case with **no prosecutor** is not one of those cases. It used to be, and the button disabled
 * itself on most of the case list as a result. Lloyd's Sep 2 instruction is that the request goes to
 * the clerk to forward instead, so the panel says who it will be addressed to rather than refusing.
 *
 * ## It downloads; it does not send
 *
 * Both documents come back as PDFs for a person to read and file. Nothing reaches the court, the
 * prosecutor or the clerk from here, and the request carries no certificate of service, because it
 * has not been served.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

interface Blocker {
  field: string;
  reason: string;
}

/** One act, two audiences: the court is told, the State is asked. Both come back together. */
interface DownloadableDocument {
  kind: string;
  fileName: string;
  pdfBase64: string;
}

interface Readiness {
  canGenerate: boolean;
  blockers: Blocker[];
  prosecutorName: string | null;
  addressee: 'prosecutor' | 'clerk';
  /** The prosecutor's name, or "the clerk of the court". Never blank. */
  addressedTo: string;
}

interface RejectOfferPanelProps {
  Ticket_guid: string;
}

declare const process: { env: { API_HOST?: string } };

function host(): string {
  return process.env.API_HOST?.trim() || window.location.origin;
}

const RejectOfferPanel: React.FC<RejectOfferPanelProps> = ({ Ticket_guid }) => {
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${host()}/api/v1/manager/ticket/${Ticket_guid}/self-executing-discovery/readiness`,
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error('Could not check whether this case is ready');
      setReadiness(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check readiness');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async () => {
    setWorking(true);
    setError(null);
    setDone(null);
    try {
      const response = await fetch(
        `${host()}/api/v1/manager/ticket/${Ticket_guid}/reject-offer-and-request-discovery`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        },
      );
      const body = await response.json();
      if (!response.ok) {
        // A 422 means nothing was written — the offer is still open. Say so, rather than leaving
        // staff unsure whether the rejection went through.
        throw new Error(
          body.message ||
            'Could not produce the discovery request, so the offer was not rejected.',
        );
      }

      // Straight to the browser. The PDFs never touch our storage and are never emailed from here.
      // The link has to be in the document before it is clicked — a detached one is ignored by
      // Firefox, which would drop both PDFs while this panel reported success.
      const documents: DownloadableDocument[] = body.documents ?? [];
      for (const doc of documents) {
        const bytes = Uint8Array.from(atob(doc.pdfBase64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.fileName || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      setDone(
        `${body.offerRejected ? 'Offer rejected.' : 'No open offer was recorded, so none was rejected.'} ` +
          `Jury trial requested ${body.juryTrialRequestedAt}. Two documents have downloaded: the ` +
          `notice of intent to proceed to trial, to file with the court, and the discovery ` +
          `request to ${body.addressedTo}. Send both, then record service on the discovery list.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        Only offer is a guilty plea?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rejects the offer, records that we have asked for a jury trial, and produces two documents:
        the notice of intent to proceed to trial for the court, and the Article 39.14 request for
        all the evidence. Nothing is sent — both download for you to check and file.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {done && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDone(null)}>
          {done}
        </Alert>
      )}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking this case…
          </Typography>
        </Stack>
      ) : (
        <>
          <Button
            variant="contained"
            color="warning"
            disabled={working || !readiness?.canGenerate}
            onClick={run}
          >
            {working ? 'Working…' : 'Reject offer and request discovery'}
          </Button>

          {readiness && !readiness.canGenerate && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This case is not ready yet:
              </Typography>
              {readiness.blockers.map((blocker) => (
                <Typography key={blocker.field} variant="body2" sx={{ mb: 0.5 }}>
                  • {blocker.reason}
                </Typography>
              ))}
            </Alert>
          )}

          {readiness?.canGenerate && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {readiness.addressee === 'clerk'
                ? 'No prosecutor on this case, so it will be addressed to the clerk of the court, ' +
                  'asking them to forward it to the prosecutor and the relevant agencies.'
                : `Will be addressed to ${readiness.addressedTo}.`}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default RejectOfferPanel;
